import clsx from 'clsx';
import { BookOpen, LogOut } from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { Avatar } from '@/components/ui';
import { usePortal } from '@/lib/portal';
import { useSession } from '@/lib/session';

/**
 * The chrome around a tenant's catalog portal.
 *
 * Deliberately thin: this is a reading room, not the application. There is no
 * authoring navigation because the API refuses those actions on a portal
 * hostname anyway — the shell simply reflects that.
 */
export function PortalShell() {
  const { session, logout } = useSession();
  const { context, organisation } = usePortal();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const branding = context?.portal?.branding;
  const accent = branding?.accentColor ?? '#2f34c9';
  const appHost = context?.appHost;

  return (
    <div className="flex min-h-full flex-col" style={{ ['--portal-accent' as string]: accent }}>
      <header className="pf-no-print sticky top-0 z-40 border-b border-ink-200 bg-white/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-3 px-4">
          <button type="button" onClick={() => navigate('/')} className="flex min-w-0 shrink items-center gap-2.5">
            {branding?.logoDataUri ? (
              <img src={branding.logoDataUri} alt="" className="max-h-8 max-w-32 object-contain" />
            ) : (
              <span className="grid size-7 shrink-0 place-items-center rounded-md text-[13px] font-bold text-white" style={{ background: accent }}>
                {(organisation ?? '?').slice(0, 1).toUpperCase()}
              </span>
            )}
            <span className="truncate text-[15px] font-semibold text-ink-900">{branding?.title || organisation}</span>
          </button>

          <nav className="ml-4 hidden items-center gap-0.5 sm:flex">
            <NavLink
              to="/"
              className={({ isActive }) =>
                clsx(
                  'flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13.5px] font-medium transition-colors',
                  isActive ? 'bg-ink-100 text-ink-900' : 'text-ink-600 hover:bg-ink-100',
                )
              }
            >
              <BookOpen className="size-4" />
              Policies
            </NavLink>
          </nav>

          {session ? (
            <div className="relative ml-auto">
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                onBlur={() => setTimeout(() => setMenuOpen(false), 150)}
                className="flex items-center gap-2 rounded-md py-1 pr-1.5 pl-2 hover:bg-ink-100"
              >
                <span className="hidden max-w-40 truncate text-[13px] text-ink-700 sm:block">{session.user.name}</span>
                <Avatar name={session.user.name} email={session.user.email} size={28} />
              </button>
              {menuOpen ? (
                <div className="pf-fade-in absolute right-0 z-50 mt-2 w-64 overflow-hidden rounded-xl border border-ink-200 bg-white shadow-xl">
                  <div className="border-b border-ink-100 px-4 py-3">
                    <p className="truncate text-[13.5px] font-semibold text-ink-900">{session.user.name}</p>
                    <p className="truncate text-[12px] text-ink-500">{session.user.email}</p>
                  </div>
                  {/*
                    Anyone who can do more than read is told where to go: the
                    portal cannot author, so pointing at it here saves a dead end.
                  */}
                  {appHost && session.capabilities.includes('document:edit') ? (
                    <a
                      href={`https://${appHost}`}
                      className="block px-4 py-2 text-[13px] text-ink-700 hover:bg-ink-50"
                      rel="noreferrer"
                    >
                      Open the full application
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={async () => {
                      setMenuOpen(false);
                      await logout();
                    }}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-left text-[13px] text-rose-600 hover:bg-rose-50"
                  >
                    <LogOut className="size-4" /> Sign out
                  </button>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-4 py-6">
        <Outlet />
      </main>

      <footer className="pf-no-print border-t border-ink-200 py-5 text-center text-[12px] text-ink-400">
        {organisation}
        {branding?.supportEmail ? (
          <>
            {' · '}
            <a href={`mailto:${branding.supportEmail}`} className="underline hover:text-ink-600">
              {branding.supportEmail}
            </a>
          </>
        ) : null}
      </footer>
    </div>
  );
}
